import _ from 'lodash';
import { DEBOUNCE } from '@/core/core.constants';
import { sqInvestigateStore, sqWorksheetStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE } from '@/core/flux.service';
import { findItemIn, getAllItems, getTrendStores } from '@/trend/trendDataHelper.utilities';
import { getDependencies } from '@/utilities/formula.utilities';
import { logError } from '@/utilities/logger';
import { formatMessage } from '@/utilities/logger.utilities';
import { isInWorkbookRouteAndWorkbookLoaded, isUserCreatedType, itemIconClass } from '@/utilities/utilities';
import { isPresentationWorkbookMode } from '@/utilities/workbookMode.utilities';

type DerivedItem = {
  id: string | number;
  name?: string;
  type?: string;
  iconClass?: string;
  isArchived?: boolean;
  color?: string;
  calculationType?: string;
  dependencies?: DerivedItem[];
  parameterOf?: DerivedItem[];
  isGenerated?: boolean;
  parentChain?: Array<{ id: string | number; isLast?: boolean }>;
  spacers?: Array<{ id: string | number; isLast?: boolean }>;
  trackId?: string;
  depth?: number;
  isLast?: boolean;
};

type DependencyResult = DerivedItem & { dependencies?: DerivedItem[] };

/**
 * Encapsulates logic for fetching, building, and dispatching an INVESTIGATE_SET_DERIVED_DATA_TREE message containing
 * the current state of the derived data tree.
 */

let items: Record<string | number, DerivedItem>;
let relationships: Record<string, Record<string, string[]>>;
let usedAsChild: Record<string, boolean>;
let relationshipSets: Record<string, Record<string, Set<string>>>;
let updateInProgress = false;

export function update(): (forceUpdate?: boolean) => void {
  /**
   * Updates the derived data tree
   */
  function updateDataTree(forceUpdate?: boolean) {
    const tabs = sqWorksheetStore.getTabset('sidebar') as { tabs?: string[]; activeTabIndex?: number } | undefined;
    const activeTab = tabs?.tabs?.[tabs.activeTabIndex ?? 0];
    if (
      !forceUpdate &&
      (!isInWorkbookRouteAndWorkbookLoaded() ||
        isPresentationWorkbookMode() ||
        updateInProgress ||
        !sqInvestigateStore.derivedDataPanelOpen ||
        activeTab !== 'investigate')
    ) {
      return;
    }

    updateInProgress = true;
    items = {};
    usedAsChild = {};
    relationships = {};
    relationshipSets = {};

    const findItemInCache = new Map<string | number, DerivedItem | undefined>();

    const detailsPaneItems = (getAllItems({}) as DerivedItem[]).filter((item) => item.calculationType);

    Promise.all(detailsPaneItems.map(createMaps))
      .then(() => buildFlatTree(detailsPaneItems, findItemInCache))
      .then((tree) => {
        flux.dispatch('INVESTIGATE_SET_DERIVED_DATA_TREE', { derivedDataTree: tree }, PUSH_IGNORE);
      })
      .finally(() => {
        updateInProgress = false;
      });
  }

  /**
   * Builds two maps that will allow us later to easily construct the derived data tree:
   *
   *  1) An "items" map, which maps each item ID to an object containing the required item properties
   *
   *  2) A "relationships" map, which is a map containing a map. The first level map maps the ID of each
   *     calculation in the details pane to the another map. The other map maps the ID of each of the
   *     calculation's formula dependencies to an array containing all the IDs of calculated items that are
   *     parameters of the key item.
   */
  function createMaps(item: DerivedItem): Promise<void> {
    return getDependencies({ id: String(item.id) })
      .then((result: DependencyResult) => {
        pickItem(result);

        // Process all dependencies synchronously since pickItem and addRelation are sync
        const dependencies = result.dependencies ?? [];
        const nonGeneratedDeps = dependencies.filter((dep: DerivedItem) => !dep.isGenerated);
        for (const dependency of nonGeneratedDeps) {
          pickItem(dependency);
          for (const pOf of dependency.parameterOf ?? []) {
            pickItem(pOf);
            addRelation(result.id, pOf.id, dependency.id);
          }
        }
      })
      .catch((e) => {
        logError(formatMessage`Couldn't fetch dependencies for derived data panel: ${e}`);
      });
  }

  /**
   * Builds a flat derived data tree data structure that is suitable for display using a non-recursive template
   *
   * @param {Object[]} detailsPaneCalculations - Array of items
   * @param {Map<string, any>} findItemInCache - Cache for findItemIn results
   */
  function buildFlatTree(
    detailsPaneCalculations: DerivedItem[],
    findItemInCache: Map<string | number, DerivedItem | undefined>,
  ) {
    return detailsPaneCalculations
      .slice()
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
      .map((item) => {
        let next: DerivedItem | undefined;
        const flatTree: DerivedItem[] = [];
        // Manual property assignment instead of _.pick to reduce allocations
        const root = _.omitBy(
          {
            id: item.id ?? '',
            name: item.name,
            type: item.type,
            iconClass: item.iconClass,
            isArchived: item.isArchived,
            color: item.color,
            depth: 0,
          },
          _.isUndefined,
        ) as DerivedItem;
        const stack: DerivedItem[] = [root];

        // Don't add a details pane item as a root node if it was used elsewhere as a child node
        if (usedAsChild[String(root.id)]) {
          return;
        }

        // Do a sorted depth first traversal to build a flat tree using the relationship maps created above
        while (stack.length) {
          next = stack.pop();
          if (!next) {
            break;
          }

          // Store parent chain before processing
          const parentChain = next.parentChain;

          // Compute spacers and trackId based on node's parentChain (without setting parent references)
          if (parentChain && parentChain.length > 0) {
            const spacers = new Array(parentChain.length);
            const trackIdParts = new Array(parentChain.length + 1);
            for (let i = 0; i < parentChain.length; i++) {
              const parent = parentChain[i];
              spacers[i] = { id: parent.id, isLast: parent.isLast };
              trackIdParts[i] = parent.id;
            }
            next.spacers = spacers;
            trackIdParts[parentChain.length] = next.id;
            next.trackId = trackIdParts.join('|');
          } else {
            next.spacers = [];
            next.trackId = String(next.id);
          }

          // Clean up temporary parentChain property
          delete next.parentChain;

          flatTree.push(next);

          const rootKey = String(root.id);
          if (relationships[rootKey]) {
            const children = getChildren(root, next, findItemInCache);
            for (let i = children.length - 1; i >= 0; i--) {
              const child = children[i];
              const index = children.length - 1 - i;
              // Build parent chain instead of setting parent reference (more efficient allocation)
              const childParentChain = parentChain ? new Array(parentChain.length + 1) : new Array(1);
              if (parentChain) {
                for (let j = 0; j < parentChain.length; j++) {
                  childParentChain[j] = parentChain[j];
                }
                childParentChain[parentChain.length] = { id: next.id, isLast: next.isLast };
              } else {
                childParentChain[0] = { id: next.id, isLast: next.isLast };
              }
              child.parentChain = childParentChain;
              const nextDepth = next.depth ?? 0;
              child.depth = nextDepth + 1;
              child.isLast = index === children.length - 1;
              stack.push(child);
            }
          }
        }

        return flatTree;
      })
      .filter((tree) => tree && tree.length > 0)
      .flat();
  }

  function pickItem(item: DerivedItem) {
    if (items[item.id]) {
      return item;
    }

    item.iconClass = itemIconClass(item);
    // Manual property assignment instead of _.pick to reduce object allocations
    items[item.id] = _.omitBy(
      {
        id: item.id,
        name: item.name,
        type: item.type,
        iconClass: item.iconClass,
        isArchived: item.isArchived,
        color: item.color,
      },
      _.isUndefined,
    ) as DerivedItem;
  }

  function addRelation(rootId: string | number, parentId: string | number, childId: string | number) {
    const rootKey = String(rootId);
    const parentKey = String(parentId);
    const childKey = String(childId);
    if (!relationships[rootKey]) {
      relationships[rootKey] = {};
      relationshipSets[rootKey] = {};
    }

    if (!relationships[rootKey][parentKey]) {
      relationships[rootKey][parentKey] = [];
      relationshipSets[rootKey][parentKey] = new Set();
    }

    // Add relationship if item is not stored and not already in the list (O(1) lookup with Set)
    const childItem = items[childKey];
    if (!relationshipSets[rootKey][parentKey].has(childKey) && childItem && isUserCreatedType(childItem.type ?? '')) {
      relationships[rootKey][parentKey].push(childKey);
      relationshipSets[rootKey][parentKey].add(childKey);
    }

    usedAsChild[childKey] = true;
  }

  function getChildren(
    root: DerivedItem,
    node: DerivedItem,
    findItemInCache: Map<string | number, DerivedItem | undefined>,
  ): DerivedItem[] {
    const trendStores = getTrendStores();
    const rootKey = String(root.id);
    const nodeKey = String(node.id);

    return (relationships[rootKey]?.[nodeKey] || [])
      .map((childId) => {
        // Shallow clone is sufficient for our use case - only need to modify color
        const baseItem = items[childId];
        if (!baseItem) {
          return undefined;
        }
        const child = { ...baseItem };

        // Cache findItemIn results to avoid repeated expensive lookups
        let detailsPaneItem = findItemInCache.get(childId);
        if (detailsPaneItem === undefined) {
          detailsPaneItem = findItemIn(trendStores, childId);
          findItemInCache.set(childId, detailsPaneItem);
        }

        if (detailsPaneItem) {
          child.color = detailsPaneItem.color;
        }
        return child;
      })
      .filter((child): child is DerivedItem => Boolean(child))
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }

  return _.debounce(updateDataTree, DEBOUNCE.SHORT);
}
